import React from 'react'
import { InputNumber, Slider, Tooltip } from 'antd'
import { useLanguage } from 'context/LanguageContext'
import {
    CryptoCalculatorDataModel,
    FuturesCalculatorDataModel,
    RiskMetricsModel,
} from 'api/http/models'
import {
    contractsAt,
    CRYPTO_DAYS_YEAR,
    factPerTrade,
    FUTURES_DAYS_YEAR,
    HistoryFacts as HistoryFactsModel,
    historyTake,
    income,
    niceStep,
    perContractAt,
} from './factsCalc'
import { tradesFactorFor } from './calc'
import factsTranslations from './factsTranslations'
import s from './Facts.module.scss'

const LOCALES: Record<string, string> = {
    ru: 'ru-RU',
    en: 'en-US',
    es: 'es-ES',
    fr: 'fr-FR',
    de: 'de-DE',
    it: 'it-IT',
    hy: 'hy-AM',
}

export const fill = (text: string, vars: Record<string, string | number>) =>
    text.replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? ''))

/** Числа и деньги на языке сайта: USDT — словом, $ / € — знаком. */
export const useFactsFormat = (currency: string) => {
    const { language } = useLanguage()
    const locale = LOCALES[language] ?? 'en-US'
    const t = factsTranslations[language] ?? factsTranslations.en
    const fmt = (v: number, digits = 0) =>
        v.toLocaleString(locale, { maximumFractionDigits: digits })
    const short = new Intl.NumberFormat(locale, {
        notation: 'compact',
        maximumFractionDigits: 1,
    })
    const sign = currency === 'EUR' ? '€' : '$'
    const usdt = currency === 'USDT'
    const money = (v: number, digits = 0) =>
        usdt
            ? `${fmt(v, digits)} USDT`
            : `${v < 0 ? '−' : ''}${sign}${fmt(Math.abs(v), digits)}`
    const compact = (v: number) =>
        usdt ? `${short.format(v)} USDT` : `${sign}${short.format(v)}`
    const dayLabel = (day: string) =>
        new Date(`${day}T00:00:00Z`).toLocaleDateString(locale, {
            weekday: 'short',
            day: '2-digit',
            month: '2-digit',
            timeZone: 'UTC',
        })
    const dateLabel = (ms: number) =>
        new Date(ms).toLocaleDateString(locale, {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            timeZone: 'UTC',
        })
    return { t, fmt, money, compact, dayLabel, dateLabel }
}

interface HistoryFactsProps {
    history: HistoryFactsModel
    riskMetrics: RiskMetricsModel | null
    crypto: boolean
    coin: string
    positionSize: number
    currency: string
    instrument: string
    /** Пометка над фактами (например, «это пример: по инструменту сделок нет»). */
    note?: string
}

/**
 * Как робот торговал на самом деле: период, сколько заработано, сделок и
 * контрактов; в среднем за день; по дням; Шарп/Сортино/снижение.
 */
export const HistoryFacts: React.FC<HistoryFactsProps> = ({
    history: h,
    riskMetrics: metrics,
    crypto,
    coin,
    positionSize,
    currency,
    instrument,
    note,
}) => {
    const { t, fmt, money, dayLabel, dateLabel } = useFactsFormat(currency)
    const lot = positionSize || 1
    const volumeLabel = crypto ? fill(t.volumeCrypto, { coin }) : t.volumeGlobal
    const hasMetrics = !!metrics && metrics.sharpe != null
    // Старый бэкенд (до деплоя) не присылает итогов и разбивки по дням.
    const hasTotals =
        typeof h.totalProfit === 'number' && typeof h.totalVolume === 'number'
    const daily = (h.daily ?? []).slice(-10)
    const dayMax = Math.max(...daily.map(x => Math.abs(x.profit)), 1)
    // Показатели для институциональных инвесторов — по фактическим дням
    // (shared/risk_metrics.py), без пересчёта в прогноз.  Нет убыточных
    // дней или сделок — Сортино, Кальмар и профит-фактор бесконечны: «∞».
    const m = hasMetrics ? metrics! : null
    const pct = (v?: number | null, digits = 1) =>
        v === null || v === undefined ? '—' : `${fmt(v * 100, digits)}%`
    const ratio = (v?: number | null, infinite = false) =>
        v !== null && v !== undefined ? fmt(v, 2) : infinite ? '∞' : '—'
    const noLossDays = !!m && (m.worstDay ?? 0) >= 0
    const noLossTrades = !!m && (m.trades ?? 0) > 0 && (m.grossLoss ?? 0) === 0
    const cards: { k: string; label: string; hint?: string; v: string }[] = [
        {
            k: 'sharpe',
            label: t.sharpe,
            hint: t.sharpeHint,
            v: ratio(m?.sharpe),
        },
        {
            k: 'sortino',
            label: t.sortino,
            hint: noLossDays ? t.noLosses : t.sortinoHint,
            v: ratio(m?.sortino, noLossDays),
        },
        {
            k: 'calmar',
            label: t.calmar,
            hint: t.calmarHint,
            v: ratio(m?.calmar, !!m && (m.maxDrawdown ?? 0) === 0),
        },
        {
            k: 'dd',
            label: t.maxDrawdown,
            hint: t.drawdownHint,
            v: pct(m?.maxDrawdown, 2),
        },
        // Отдельно — по снимкам капитала: сколько позиция была в минусе,
        // пока не закрылась (в остальных показателях только сделки).
        {
            k: 'eqdd',
            label: t.equityDrawdown,
            hint: fill(t.equityHint, { days: metrics?.equityDays ?? 0 }),
            v: pct(metrics?.equityMaxDrawdown, 2),
        },
        { k: 'vol', label: t.volatility, v: pct(m?.volatility) },
        { k: 'ret', label: t.periodReturn, v: pct(m?.periodReturn) },
        {
            k: 'pf',
            label: t.profitFactor,
            hint: noLossTrades ? t.noLosses : t.pfHint,
            v: ratio(m?.profitFactor, noLossTrades),
        },
        { k: 'wr', label: t.winRate, v: pct(m?.winRate) },
        { k: 'wd', label: t.winDays, v: pct(m?.winDays, 0) },
        { k: 'best', label: t.bestDay, v: pct(m?.bestDay, 2) },
        { k: 'worst', label: t.worstDay, v: pct(m?.worstDay, 2) },
    ]
    const basis = m
        ? fill(t.metricsBasis, {
              days: m.days,
              from: dateLabel(Date.parse(`${m.fromDay}T00:00:00Z`)),
              to: dateLabel(Date.parse(`${m.toDay}T00:00:00Z`)),
          }) + (m.source && m.source !== 'equity' ? ` · ${t.metricsDeals}` : '')
        : t.metricsFew

    return (
        <div className={s.panel}>
            <div className={s.panelHead}>
                <h3>{t.factsTitle}</h3>
                <span className={s.instrument}>{instrument}</span>
            </div>
            {note && <p className={s.note}>{note}</p>}
            {h.fromMs && h.toMs ? (
                <p className={s.muted}>
                    {fill(t.period, {
                        from: dateLabel(h.fromMs),
                        to: dateLabel(h.toMs),
                        days: fmt(h.accountDays, 1),
                        accounts: h.accounts,
                    })}
                </p>
            ) : null}

            {hasTotals && (
                <dl className={s.totals}>
                    <div className={s.totalMain}>
                        <dt>{t.earned}</dt>
                        <dd>{money(h.totalProfit)}</dd>
                        <p>{t.earnedHint}</p>
                    </div>
                    <div>
                        <dt>{t.deals}</dt>
                        <dd>{fmt(h.deals)}</dd>
                    </div>
                    <div>
                        <dt>{volumeLabel}</dt>
                        <dd>{fmt(h.totalVolume, 2)}</dd>
                    </div>
                </dl>
            )}

            <div className={s.perDay}>
                <div className={s.subTitle}>{t.perDayTitle}</div>
                <dl>
                    <div>
                        <dt>{t.deals}</dt>
                        <dd>{fmt(h.tradesPerDay)}</dd>
                    </div>
                    <div>
                        <dt>{volumeLabel}</dt>
                        <dd>{fmt(h.volumePerLotDay * lot, 2)}</dd>
                    </div>
                    <div>
                        <dt>{t.earned}</dt>
                        <dd className={s.good}>
                            {money(h.netPerLotDay * lot)}
                        </dd>
                    </div>
                </dl>
                {!crypto && h.volumePerLotDay > 0 && (
                    <p className={s.muted}>
                        {money(h.netPerLotDay / h.volumePerLotDay, 2)}{' '}
                        {t.perContract} · {t.perLot}
                    </p>
                )}
            </div>

            {daily.length > 0 && (
                <div className={s.days}>
                    <div className={s.subTitle}>{t.byDays}</div>
                    {daily.map(x => (
                        <div className={s.dayRow} key={x.day}>
                            <span className={s.dayDate}>{dayLabel(x.day)}</span>
                            <span className={s.dayBar}>
                                <i
                                    data-neg={x.profit < 0}
                                    style={{
                                        width: `${
                                            (Math.abs(x.profit) / dayMax) * 100
                                        }%`,
                                    }}
                                />
                            </span>
                            <span className={s.dayNums}>
                                {fmt(x.deals)} {t.dealsShort} ·{' '}
                                {fmt(x.volume, 2)}{' '}
                                {crypto ? coin : t.contractsShort}
                            </span>
                            <b className={x.profit < 0 ? s.bad : s.good}>
                                {money(x.profit)}
                            </b>
                        </div>
                    ))}
                </div>
            )}

            <div className={s.subTitle}>{t.metricsTitle}</div>
            <p className={s.muted}>{basis}</p>
            <dl className={s.metrics}>
                {cards.map(c => (
                    <div key={c.k}>
                        <dt>
                            {c.hint ? (
                                <Tooltip title={c.hint}>
                                    <span className={s.hinted}>{c.label}</span>
                                </Tooltip>
                            ) : (
                                c.label
                            )}
                        </dt>
                        <dd>{c.v}</dd>
                    </div>
                ))}
            </dl>
        </div>
    )
}

/** Ползунки посетителя; undefined — стоит факт (или значение по умолчанию). */
export interface Knobs {
    deposit?: number
    lot?: number
    take?: number
    perDay?: number // контрактов на 1 лот (фьючерсы) или сделок (крипта) в день
}

interface CheckProps {
    data: FuturesCalculatorDataModel | CryptoCalculatorDataModel
    crypto: boolean
    coin: string
    currency: string
    knobs: Knobs
    onChange: (next: Knobs) => void
    defaultDeposit: number
    idPrefix: string
    /** Под результатом: кнопка калькулятора / регистрации / сохранения. */
    footer?: React.ReactNode
}

/**
 * Проверьте сами: депозит, лот, тейк (фьючерсы) и число контрактов или
 * сделок в день — ползунки стоят на фактах, доход считается по factsCalc.
 */
export const CheckYourself: React.FC<CheckProps> = ({
    data,
    crypto,
    coin,
    currency,
    knobs,
    onChange,
    defaultDeposit,
    idPrefix,
    footer,
}) => {
    const { t, fmt, money, compact } = useFactsFormat(currency)
    const h = data.history
    if (!h) return null
    const set = (patch: Knobs) => onChange({ ...knobs, ...patch })

    const deposit = knobs.deposit ?? defaultDeposit
    const depositMax = Math.max(defaultDeposit * 5, deposit)
    const depositStep = niceStep(depositMax)
    const lot = knobs.lot ?? data.positionSize
    const futures = !crypto ? (data as FuturesCalculatorDataModel) : null

    // Тейк: по умолчанию — тот, с которым набран факт.  Другой тейк — каждая
    // сделка больше (меньше) на разницу тейков, а сделок меньше (больше).
    const histTp = historyTake(h, data.takeProfit)
    const take = knobs.take ?? histTp
    const takeChanged = Math.abs(take - histTp) > 1e-12
    const tpDigits = crypto ? 6 : 2
    const takeLabel = crypto
        ? `${fmt(take, tpDigits)} ${currency}`
        : `${fmt(take, tpDigits)} ${t.pts} · ${money(
              take * futures!.pointValue
          )}`

    let robotDay: number
    let formula: React.ReactNode
    let perDay: number
    let factPerDay: number
    let perDayLabel: string
    let perDayFact: number
    if (futures) {
        const lots = lot / Math.max(futures.positionSize, 1)
        factPerDay = contractsAt(h, take, histTp)
        perDay = knobs.perDay ?? factPerDay
        perDayFact = h.volumePerLotDay
        perDayLabel = t.contractsDay
        const per = perContractAt(h, take, histTp, futures.pointValue)
        robotDay = perDay * lots * per
        formula = (
            <>
                {fmt(perDay * lots)} {t.contractsShort} × {money(per, 2)}{' '}
                {t.perContract}
            </>
        )
    } else {
        factPerDay = takeChanged
            ? h.tradesPerDay * tradesFactorFor(take, histTp)
            : h.tradesPerDay
        perDay = knobs.perDay ?? factPerDay
        perDayFact = h.tradesPerDay
        perDayLabel = t.tradesDay
        // Чистыми на сделку на 1 монету лота: факт + разница тейков (в цене).
        const per = (factPerTrade(h) + (take - histTp)) * lot
        robotDay = perDay * per
        formula = (
            <>
                {fmt(perDay)} {t.dealsShort} × {money(per, 2)} {t.perTrade}
            </>
        )
    }
    const takeNote = takeChanged
        ? fill(t.takeNote, {
              hist: crypto
                  ? `${fmt(histTp, tpDigits)} ${currency}`
                  : `${fmt(histTp, tpDigits)} ${t.pts}`,
          })
        : null
    const takeRow = (
        <div className={s.knob}>
            <div className={s.knobHead}>
                <label htmlFor={`${idPrefix}-take`}>
                    {crypto ? t.takePrice : t.take}
                </label>
                <b>{takeLabel}</b>
            </div>
            <Slider
                id={`${idPrefix}-take`}
                min={Math.min(data.tpMin, take)}
                max={Math.max(data.tpMax, take)}
                step={data.tpStep || (crypto ? undefined : 0.25)}
                value={take}
                marks={{ [histTp]: ' ' }}
                tooltip={{
                    formatter: v => (v === undefined ? '' : fmt(v, tpDigits)),
                }}
                // Другой тейк — число сделок снова от факта (с поправкой на тейк).
                onChange={(v: number) => set({ take: v, perDay: undefined })}
            />
        </div>
    )
    const r = income(
        robotDay,
        deposit,
        crypto ? CRYPTO_DAYS_YEAR : FUTURES_DAYS_YEAR
    )
    const touched = Object.values(knobs).some(v => v !== undefined)

    return (
        <div className={s.panel}>
            <div className={s.panelHead}>
                <h3>{t.checkTitle}</h3>
                {touched && (
                    <button
                        type="button"
                        className={s.reset}
                        onClick={() => onChange({})}
                    >
                        {t.reset}
                    </button>
                )}
            </div>
            <p className={s.muted}>{t.checkHint}</p>

            <div className={s.knob}>
                <div className={s.knobHead}>
                    <label htmlFor={`${idPrefix}-deposit`}>{t.deposit}</label>
                    <InputNumber
                        id={`${idPrefix}-deposit`}
                        className={s.depositInput}
                        size="small"
                        value={deposit}
                        min={0}
                        step={depositStep}
                        controls={false}
                        formatter={v =>
                            `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
                        }
                        parser={v => Number((v ?? '').replace(/\s/g, ''))}
                        onChange={v =>
                            v !== null && set({ deposit: Number(v) })
                        }
                    />
                </div>
                <Slider
                    min={depositStep}
                    max={depositMax}
                    step={depositStep}
                    value={deposit}
                    tooltip={{ formatter: v => (v ? compact(v) : '') }}
                    onChange={(v: number) => set({ deposit: v })}
                />
            </div>

            <div className={s.knob}>
                <div className={s.knobHead}>
                    <label htmlFor={`${idPrefix}-lot`}>{t.lot}</label>
                    <b>
                        {fmt(lot, 4)} {crypto ? coin : t.contractsShort}
                    </b>
                </div>
                <Slider
                    id={`${idPrefix}-lot`}
                    min={data.lotMin}
                    max={Math.max(data.lotMax, lot)}
                    step={data.lotStep || 1}
                    value={lot}
                    onChange={(v: number) => set({ lot: v })}
                />
            </div>

            {takeRow}

            <div className={s.knob}>
                <div className={s.knobHead}>
                    <label htmlFor={`${idPrefix}-per-day`}>{perDayLabel}</label>
                    <b>{fmt(perDay)}</b>
                </div>
                <Slider
                    id={`${idPrefix}-per-day`}
                    min={0}
                    max={Math.ceil(Math.max(perDayFact * 2, perDay, 1))}
                    step={1}
                    value={Math.round(perDay)}
                    marks={{ [Math.round(factPerDay)]: ' ' }}
                    onChange={(v: number) => set({ perDay: v })}
                />
                <span className={s.factMark}>
                    {fill(t.fact, { v: fmt(perDayFact) })}
                </span>
            </div>

            {takeNote && <p className={s.note}>{takeNote}</p>}

            <div className={s.result}>
                <div className={s.formula}>
                    <span>{t.robotDay}</span>
                    <span>
                        {formula} = <b>{money(r.robotDay)}</b>
                    </span>
                </div>
                <div className={s.share}>{t.yourShare}</div>
                <dl className={s.income}>
                    <div>
                        <dt>{t.perDay}</dt>
                        <dd>{money(r.clientDay)}</dd>
                    </div>
                    <div>
                        <dt>{t.perMonth}</dt>
                        <dd>{compact(r.clientMonth)}</dd>
                    </div>
                    <div>
                        <dt>{t.perYear}</dt>
                        <dd>{compact(r.clientYear)}</dd>
                    </div>
                </dl>
                {r.yearPct !== null && (
                    <div className={s.yearPct}>
                        {fill(t.yearPct, { pct: fmt(r.yearPct) })}
                    </div>
                )}
            </div>

            {footer}
        </div>
    )
}
